import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, IconButton, CircularProgress, Button, Tooltip, Typography } from '@mui/material';
import { ChevronLeft, Copy, RefreshCw, ScrollText, X } from 'lucide-react';
import RunDetailView from './RunDetailView';
import { unwrapRunDetail, runDetailErrorMessage, runTitle } from './runDetailFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './RunDetail.module.scss';

/**
 * Run Details, as a right-side drawer shared by all three surfaces (Agent UI
 * History, Company Admin usage drill-in, Platform Admin usage drill-in).
 *
 * The surface differences are injected, not branched on:
 *   - `fetchDetail(runId)` — which endpoint to call;
 *   - `runId` / `runHint` — the row that was clicked (the hint renders the
 *     header before the fetch resolves, and after a 404).
 *
 * Chain nodes and team HIL legs come back as `children`, each a full RunSummary
 * with its own detail — so opening one PUSHES onto a stack and Back pops it,
 * rather than replacing the drawer.
 */
const RunDetailDrawer = ({ open, runId, runHint, fetchDetail, onClose, onToast, redactedMessage = null }) => {
  // Breadcrumb stack: [{ runId, hint }], last entry is what's displayed.
  const [stack, setStack] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const current = stack[stack.length - 1] || null;

  // A new root run resets the stack; closing clears it so a reopen never shows
  // the previous run's trail.
  useEffect(() => {
    if (open && runId) {
      setStack([{ runId, hint: runHint || null }]);
    } else if (!open) {
      setStack([]);
      setDetail(null);
      setError(null);
    }
    // runHint is intentionally not a dep — it's a snapshot of the clicked row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, runId]);

  const load = useCallback(async () => {
    if (!current?.runId || !fetchDetail) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDetail(current.runId);
      setDetail(unwrapRunDetail(data));
    } catch (err) {
      setError({ message: runDetailErrorMessage(err), status: err?.status });
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [current?.runId, fetchDetail]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const openChild = useCallback((childRunId, childHint) => {
    if (!childRunId) return;
    setStack((prev) => [...prev, { runId: childRunId, hint: childHint || null }]);
  }, []);

  const goBack = useCallback(() => setStack((prev) => prev.slice(0, -1)), []);

  const headerRun = detail?.run || current?.hint || null;
  const title = useMemo(() => (headerRun ? runTitle(headerRun) : 'Run details'), [headerRun]);

  const copyRunId = async () => {
    if (!current?.runId) return;
    try {
      await navigator.clipboard.writeText(String(current.runId));
      onToast?.('Run ID copied', 'success');
    } catch {
      onToast?.('Failed to copy run ID', 'error');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: styles.drawerPaper }}
      // The admin surfaces open this from inside the runs Dialog; a Drawer's
      // default z-index sits below a Dialog's, which would hide it entirely.
      sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
    >
      <div className={styles.drawerHeader}>
        <div className={styles.drawerHeaderMain}>
          {stack.length > 1 && (
            <button type="button" className={styles.crumbBtn} onClick={goBack}>
              <ChevronLeft size={13} aria-hidden />
              Back to parent run
            </button>
          )}
          <h3 className={styles.drawerTitle}>{title}</h3>
          {current?.runId && (
            <span className={styles.runId}>
              {current.runId}
              <Tooltip title="Copy run ID" arrow>
                <button type="button" className={styles.copyBtn} onClick={copyRunId}>
                  <Copy size={12} aria-hidden />
                </button>
              </Tooltip>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <Tooltip title="Reload" arrow>
            <span>
              <IconButton size="small" className={sharedStyles.subtleBtn} onClick={load} disabled={loading}>
                <RefreshCw size={16} />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" className={sharedStyles.subtleBtn} onClick={onClose} aria-label="Close">
            <X size={18} />
          </IconButton>
        </div>
      </div>

      <div className={styles.drawerBody}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
            <CircularProgress color="primary" />
          </div>
        ) : error ? (
          <div className={sharedStyles.emptyState}>
            <ScrollText className={sharedStyles.emptyIcon} size={44} />
            <Typography variant="h6">
              {error.status === 404 ? 'Run not found' : 'Could not load this run'}
            </Typography>
            <Typography variant="body2">{error.message}</Typography>
            {error.status !== 404 && (
              <Button className={sharedStyles.outlineBtn} onClick={load} sx={{ mt: 1 }}>
                Try again
              </Button>
            )}
          </div>
        ) : detail ? (
          <RunDetailView detail={detail} onOpenChild={openChild} redactedMessage={redactedMessage} />
        ) : (
          // A row can legitimately have no trace: it ran before the feature
          // shipped, its trace aged out of the 90-day retention window, or
          // tracing is switched off server-side.
          <div className={sharedStyles.emptyState}>
            <ScrollText className={sharedStyles.emptyIcon} size={44} />
            <Typography variant="h6">No detail for this run</Typography>
            <Typography variant="body2">
              Run details are kept for 90 days and only exist for runs recorded since the feature
              shipped. This run's metrics are still accurate.
            </Typography>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default RunDetailDrawer;
